import React, { useState } from 'react';
import FilesApi, {
  FileResponseDto,
  UploadFileDto,
} from '@/api/files';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
  UISectionFooter,
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIInput from '@/components/shared/UIInput';
import UIButton from '@/components/shared/UIButton';
import UIIconButton from '@/components/shared/UIIconButton';
import FileUploader from '@/components/shared/FileUploader';
import UIIconExtraButton from '@/components/shared/UIIconExtraButton';
import './DocumentsSection.scss';

export interface DocumentsSectionProps {
  className?: string;
  documents: FileResponseDto[];
  onUpload: (file: File) => Promise<void>;
  onUpdate: (id: number, newName: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  loading?: boolean;
  error?: string | null;
}

const baseClass = 'DocumentsSection';

const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  className = '',
  documents,
  onUpload,
  onUpdate,
  onDelete,
  loading = false,
  error = null,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editingFileName, setEditingFileName] = useState<string>('');

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleFilesSelect = async (files: File[]) => {
    for (const file of files) {
      await onUpload(file);
    }
    setUploadedFiles([]);
  };

  const handleEdit = (id: number) => {
    const document = documents.find((doc) => doc.id === id);
    if (document) {
      setEditingFileId(id);
      setEditingFileName(document.originalName);
    }
  };

  const handleCancelEdit = () => {
    setEditingFileId(null);
    setEditingFileName('');
  };

  const handleSaveEdit = async (id: number) => {
    await onUpdate(id, editingFileName);
    setEditingFileId(null);
    setEditingFileName('');
  };

  const handleView = (id: number) => {
    const document = documents.find((doc) => doc.id === id);
    if (document) {
      window.open(document.url, '_blank');
    }
  };

  const getFileTypeFromMime = (mimeType: string): string => {
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'DOC';
    if (mimeType.includes('image')) return 'IMG';
    return 'FILE';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const headerAction = (
    <UIIconExtraButton
      icon={isCollapsed ? 'expand' : 'collapse'}
      onClick={handleToggleCollapse}
      ariaLabel={isCollapsed ? 'Expand documents' : 'Collapse documents'}
    />
  );

  return (
    <UISectionWrapper
      className={`${baseClass} ${className}`}
      layout="default"
      formPadding="large"
      align="stretch"
    >
      <UISectionHeader>
        <UISectionTitle
          title="Documents"
          action={headerAction}
          showDivider={true}
        />
      </UISectionHeader>

      {!isCollapsed && (
        <>
          <UISectionBody>
            {error && (
              <div style={{ color: 'red', marginBottom: '16px' }}>{error}</div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '32px' }}>
                Loading documents...
              </div>
            ) : (
              <div className={`${baseClass}__table-container`}>
                <div className={`${baseClass}__table`}>
                  <div className={`${baseClass}__table-header`}>
                    <span className={`${baseClass}__header-cell`}>Type</span>
                    <span className={`${baseClass}__header-cell`}>Document Info</span>
                    <span className={`${baseClass}__header-cell`}>Filename</span>
                    <span className={`${baseClass}__header-cell`}>Actions</span>
                  </div>

                  <div className={`${baseClass}__table-body`}>
                    {documents.length === 0 ? (
                      <div className={`${baseClass}__empty-state`}>
                        No documents uploaded yet
                      </div>
                    ) : (
                      documents.map((document) => (
                        <div
                          key={document.id}
                          className={`${baseClass}__table-row`}
                        >
                          <div className={`${baseClass}__cell ${baseClass}__cell--type`}>
                            {getFileTypeFromMime(document.mimeType)}
                          </div>

                          <div className={`${baseClass}__cell ${baseClass}__cell--info`}>
                            {formatFileSize(document.fileSize)}
                          </div>

                          <div className={`${baseClass}__cell ${baseClass}__cell--filename`}>
                            <UIInput
                              value={
                                editingFileId === document.id
                                  ? editingFileName
                                  : document.originalName
                              }
                              onChange={(e) => {
                                if (editingFileId === document.id) {
                                  setEditingFileName(e.target.value);
                                }
                              }}
                              className={`${baseClass}__filename-input`}
                              readOnly={editingFileId !== document.id}
                            />
                          </div>

                          <div className={`${baseClass}__cell ${baseClass}__cell--actions`}>
                            <div className={`${baseClass}__action-buttons`}>
                              {editingFileId === document.id ? (
                                <>
                                  <UIButton
                                    variant="primary"
                                    size="small"
                                    onClick={() => handleSaveEdit(document.id)}
                                  >
                                    Save
                                  </UIButton>
                                  <UIButton
                                    variant="secondary"
                                    size="small"
                                    onClick={handleCancelEdit}
                                  >
                                    Cancel
                                  </UIButton>
                                </>
                              ) : (
                                <>
                                  <UIButton
                                    variant="primary"
                                    size="small"
                                    onClick={() => handleEdit(document.id)}
                                  >
                                    Edit
                                  </UIButton>
                                  <UIIconButton
                                    variant="info"
                                    size="medium"
                                    iconType="doc"
                                    onClick={() => handleView(document.id)}
                                  />
                                  <UIIconButton
                                    variant="secondary"
                                    size="medium"
                                    iconType="delete"
                                    onClick={() => onDelete(document.id)}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </UISectionBody>

          <UISectionFooter>
            <div className={`${baseClass}__upload-container`}>
              <FileUploader
                onFilesSelect={handleFilesSelect}
                files={uploadedFiles}
                acceptFiles={[
                  'application/pdf',
                  'image/jpeg',
                  'image/png',
                  'application/msword',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                ]}
                filesLimit={5}
                maxFileSize={10 * 1024 * 1024} // 10MB
                filesDescription="DOC, DOCX, PDF, JPG, PNG / max 10 MB"
                className={`${baseClass}__file-uploader`}
              />
            </div>
          </UISectionFooter>
        </>
      )}
    </UISectionWrapper>
  );
};

export default DocumentsSection;
